import { Clock, PerspectiveCamera, Scene, WebGLRenderer } from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass';
import { CustomBloomPass } from '../post/CustomBloomPass';
import { CustomFXAAShader } from '../post/CustomFXAAShader';

export default class AppEngine {
  constructor(_canvas, _canvasWidth, _canvasHeight) {
    this.scene = this.initScene();
    this.camera = this.initCamera(_canvasWidth, _canvasHeight);
    this.renderer = this.initRenderer(_canvas, _canvasWidth, _canvasHeight);
    this.info = this.initInfo();

    if (process.env.VUE_APP_POST_PROCESS != 'false' || process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line no-console
      // console.log('init composer');
      this.composer = this.initComposer(this.renderer, this.scene, this.camera);
    }

    this.clock = new Clock();

    return this;
  }

  initScene() {
    const scene = new Scene();
    return scene;
  }

  initCamera(_width, _height) {
    const camera = new PerspectiveCamera(45, _width / _height, 0.1, 1000);

    camera.position.set(0, 0, 10);
    return camera;
  }

  initRenderer(canvas, _width, _height) {
    // Turn of antialias - post FXAA handles that
    const renderer = new WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      powerPreference: 'high-performance',
    });

    renderer.isEnabled = true;
    renderer.shadowMap.autoUpdate = false;
    renderer.setSize(_width, _height);
    renderer.setPixelRatio(window.devicePixelRatio || 1);

    return renderer;
  }

  initComposer(renderer, scene, camera) {
    const bloomPass = new CustomBloomPass(3.0, 0.7);

    this.fxaaPass = new ShaderPass(CustomFXAAShader);

    const pixelRatio = this.renderer.getPixelRatio();

    this.fxaaPass.material.uniforms.exposure.value = 1 / AppEngine.HDR_SCALE;
    this.fxaaPass.material.uniforms.resolution.value.x = 1 / (window.innerWidth * pixelRatio);
    this.fxaaPass.material.uniforms.resolution.value.y = 1 / (window.innerHeight * pixelRatio);

    const renderScene = new RenderPass(scene, camera);
    const composer = new EffectComposer(renderer);
    composer.addPass(renderScene);
    composer.addPass(bloomPass);
    composer.addPass(this.fxaaPass);
    return composer;
  }

  render() {
    if (this.composer) {
      const { mask } = this.camera.layers;
      const { autoClear } = this.renderer;
      this.composer.render();

      this.camera.layers.set(2);
      this.renderer.autoClear = false;
      this.renderer.clearDepth();
      this.renderer.render(this.scene, this.camera);
      this.renderer.autoClear = autoClear;

      this.camera.layers.mask = mask;
    } else {
      this.camera.layers.enableAll();
      this.renderer.render(this.scene, this.camera);
    }
  }

  stopRenderer() {
    this.renderer.isEnabled = false;
  }

  startRenderer() {
    this.renderer.isEnabled = true;
  }

  addToScene(_object) {
    this.scene.add(_object);
  }

  onResize(_width, _height) {
    this.renderer.setSize(_width, _height);
    this.camera.aspect = _width / _height;
    this.camera.updateProjectionMatrix();

    const pixelRatio = this.renderer.getPixelRatio();

    if (this.composer) {
      this.composer.setSize(_width, _height);
      this.fxaaPass.material.uniforms.resolution.value.x = 1 / (_width * pixelRatio);
      this.fxaaPass.material.uniforms.resolution.value.y = 1 / (_height * pixelRatio);
    }
  }

  initInfo() {
    const gl = this.renderer.getContext();
    const ext = this.renderer.extensions;
    const debugInfo = ext.get('WEBGL_debug_renderer_info');
    return debugInfo
      ? {
          vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
          renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
        }
      : {
          vendor: 'unknown',
          renderer: 'unknown',
        };
  }
}

AppEngine.HDR_SCALE = 0.75;
